import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
 Input, Button, Space, Table, Modal, Form, DatePicker,
  Tooltip, Typography, Select, Progress, Switch, Alert, Avatar
} from "antd";
import { useDispatch } from "react-redux";
import axios from "axios";
import moment from "moment";
import toast from "react-hot-toast";
import Layout from "../../components/layout";
import { ruleToMatcher } from "../../components/utils/clinicDay";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  SearchOutlined, FileExcelOutlined, FilePdfOutlined, FilterOutlined,
  ClearOutlined, CheckCircleOutlined, CloseCircleOutlined, CalendarOutlined,
   WarningOutlined, InfoCircleOutlined
} from "@ant-design/icons";

const { Text, Title } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Status helpers ──────────────────────────────────────────── */
const STATUS_CONFIG = {
  pending:   { color: "#b45309", bg: "#fef9c3", progress: 30,  bar: "#faad14" },
  approved:  { color: EMERALD,   bg: TEAL_LIGHT, progress: 60, bar: TEAL      },
  "follow-up":{ color: "#7c3aed", bg: "#f5f3ff", progress: 90, bar: "#8b5cf6" },
  completed: { color: "#16a34a", bg: "#dcfce7",  progress: 100,bar: "#22c55e" },
  rejected:  { color: "#dc2626", bg: "#fee2e2",  progress: 0,  bar: "#ef4444" },
};
const getStatusCfg   = (s) => STATUS_CONFIG[s] || { color: MUTED, bg: SURFACE, progress: 0, bar: "#d9d9d9" };
const getStatusProgress = (s) => getStatusCfg(s).progress;


/* ─── Style injection ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("appt-admin-styles")) return;
  const el = document.createElement("style");
  el.id = "appt-admin-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .aa-root { font-family:'DM Sans',sans-serif; background:${SURFACE}; min-height:100vh; }
    .aa-wrap { max-width:1280px; margin:0 auto; padding:32px 24px 64px; }

    /* Hero */
    .aa-hero {
      background:linear-gradient(135deg,${SLATE} 0%,${SLATE_MID} 100%);
      border-radius:24px; padding:36px 44px;
      position:relative; overflow:hidden; margin-bottom:28px;
    }
    .aa-hero::before {
      content:''; position:absolute; top:-80px; right:-80px;
      width:300px; height:300px; border-radius:50%;
      background:radial-gradient(circle,${TEAL}44 0%,transparent 70%);
      filter:blur(32px); pointer-events:none;
    }
    .aa-hero::after {
      content:''; position:absolute; bottom:-60px; left:-40px;
      width:200px; height:200px; border-radius:50%;
      background:radial-gradient(circle,${EMERALD}33 0%,transparent 70%);
      filter:blur(26px); pointer-events:none;
    }
    .aa-eyebrow {
      display:inline-block; padding:4px 12px; border-radius:20px;
      background:${TEAL_LIGHT}; color:${EMERALD};
      font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase;
      font-family:'Sora',sans-serif; margin-bottom:12px;
    }
    .aa-hero-badges { display:flex; gap:8px; flex-wrap:wrap; margin-top:12px; }
    .aa-hero-badge {
      background:rgba(255,255,255,0.12); border-radius:20px;
      padding:4px 12px; font-size:12px; color:rgba(255,255,255,0.75);
      font-family:'DM Sans',sans-serif;
    }

    /* Toolbar */
    .aa-toolbar {
      background:${WHITE}; border-radius:16px;
      box-shadow:0 4px 20px rgba(0,0,0,.06);
      padding:16px 20px; margin-bottom:20px;
      display:flex; align-items:center; gap:12px; flex-wrap:wrap;
    }

    /* Search */
    .aa-root .aa-search.ant-input-affix-wrapper {
      border-radius:10px !important; height:40px !important;
      border:1.5px solid #e2e8f0 !important; flex:1; min-width:200px;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s,box-shadow .2s !important;
    }
    .aa-root .aa-search.ant-input-affix-wrapper:hover,
    .aa-root .aa-search.ant-input-affix-wrapper-focused {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }
    .aa-root .aa-search .ant-input { font-family:'DM Sans',sans-serif !important; font-size:13px !important; }

    /* Status select */
    .aa-root .aa-status-select .ant-select-selector {
      border-radius:10px !important; height:40px !important;
      border:1.5px solid #e2e8f0 !important;
      display:flex !important; align-items:center !important;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s,box-shadow .2s !important;
    }
    .aa-root .aa-status-select .ant-select-selection-item,
    .aa-root .aa-status-select .ant-select-selection-placeholder { line-height:38px !important; }
    .aa-root .aa-status-select.ant-select-focused .ant-select-selector,
    .aa-root .aa-status-select .ant-select-selector:hover {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }

    /* Toolbar buttons */
    .aa-btn {
      height:40px !important; padding:0 16px !important; border-radius:10px !important;
      font-size:13px !important; font-family:'DM Sans',sans-serif !important; font-weight:600 !important;
      border:1.5px solid #e2e8f0 !important; background:${WHITE} !important;
      color:${SLATE_MID} !important; transition:border-color .2s,color .2s !important; display:flex !important; align-items:center !important; gap:6px !important;
    }
    .aa-btn:hover { border-color:${TEAL} !important; color:${TEAL} !important; }
    .aa-btn-excel:hover { border-color:#16a34a !important; color:#16a34a !important; }
    .aa-btn-pdf:hover   { border-color:#dc2626 !important; color:#dc2626 !important; }

    /* Table */
    .aa-table-wrap {
      background:${WHITE}; border-radius:20px;
      box-shadow:0 4px 20px rgba(0,0,0,.06); overflow:hidden;
    }
    .aa-root .ant-table-thead > tr > th {
      background:${SURFACE} !important; color:${SLATE_MID} !important;
      font-size:11px !important; font-weight:700 !important;
      letter-spacing:.5px; text-transform:uppercase;
      border-bottom:1.5px solid #f1f5f9 !important;
      font-family:'Sora',sans-serif !important; padding:14px 16px !important;
    }
    .aa-root .ant-table-tbody > tr > td {
      font-size:13px !important; font-family:'DM Sans',sans-serif !important;
      color:${SLATE_MID} !important; padding:12px 16px !important;
      border-bottom:1px solid #f8fafc !important;
    }
    .aa-root .ant-table-tbody > tr:hover > td { background:#f0fdfb !important; }
    .aa-root .ant-pagination { padding:16px !important; }
    .aa-root .ant-pagination-item-active { border-color:${TEAL} !important; }
    .aa-root .ant-pagination-item-active a { color:${TEAL} !important; }

    /* Status badge */
    .aa-badge {
      padding:3px 10px; border-radius:20px;
      font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
      display:inline-block;
    }

    /* Action buttons */
    .aa-approve-btn {
      height:30px !important; padding:0 12px !important; border-radius:8px !important;
      font-size:12px !important; font-weight:700 !important; border:1.5px solid ${TEAL} !important;
      color:${TEAL} !important; background:${WHITE} !important;
      font-family:'DM Sans',sans-serif !important; transition:all .2s !important;
    }
    .aa-approve-btn:hover { background:${TEAL} !important; color:${WHITE} !important; }
    .aa-reject-btn {
      height:30px !important; padding:0 12px !important; border-radius:8px !important;
      font-size:12px !important; font-weight:700 !important; border:1.5px solid #ef4444 !important;
      color:#ef4444 !important; background:${WHITE} !important;
      font-family:'DM Sans',sans-serif !important; transition:all .2s !important;
    }
    .aa-reject-btn:hover { background:#ef4444 !important; color:${WHITE} !important; }

    /* ── Modal ── */
    .aa-modal .ant-modal-content { border-radius:20px !important; overflow:hidden; padding:0 !important; }
    .aa-modal .ant-modal-header {
      padding:20px 28px !important; border-radius:0 !important; margin:0 !important;
    }
    .aa-modal.approve-modal .ant-modal-header { background:linear-gradient(135deg,${SLATE},${SLATE_MID}) !important; }
    .aa-modal.reject-modal  .ant-modal-header { background:linear-gradient(135deg,#7f1d1d,#dc2626) !important; }
    .aa-modal .ant-modal-title {
      color:${WHITE} !important; font-family:'Sora',sans-serif !important;
      font-weight:700 !important; font-size:16px !important;
    }
    .aa-modal .ant-modal-close { color:rgba(255,255,255,.5) !important; top:18px !important; }
    .aa-modal .ant-modal-close:hover { color:${WHITE} !important; }
    .aa-modal .ant-modal-body { padding:24px 28px !important; }
    .aa-modal .ant-modal-footer { padding:16px 28px !important; border-top:1.5px solid #f1f5f9 !important; }

    /* Modal fields */
    .aa-modal .ant-form-item { margin-bottom:16px !important; }
    .aa-modal .ant-form-item-label { padding-bottom:4px !important; }
    .aa-modal .ant-form-item-label > label {
      font-size:12px !important; font-weight:600 !important;
      color:${SLATE_MID} !important; height:20px !important;
    }
    .aa-modal textarea.ant-input {
      border-radius:10px !important; border:1.5px solid #e2e8f0 !important;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      padding:10px 12px !important; transition:border-color .2s !important;
    }
    .aa-modal textarea.ant-input:hover, .aa-modal textarea.ant-input:focus {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }
    .aa-modal .ant-picker {
      border-radius:10px !important; height:44px !important;
      border:1.5px solid #e2e8f0 !important; width:100% !important;
      font-family:'DM Sans',sans-serif !important; font-size:13px !important;
      transition:border-color .2s !important;
    }
    .aa-modal .ant-picker:hover, .aa-modal .ant-picker-focused {
      border-color:${TEAL} !important; box-shadow:0 0 0 3px ${TEAL}22 !important;
    }

    /* Date mode toggle strip */
    .aa-date-mode-strip {
      background:${SURFACE}; border:1.5px solid #f1f5f9;
      border-radius:12px; padding:14px 16px; margin-bottom:16px;
      display:flex; align-items:center; justify-content:space-between; gap:12px;
    }
    .aa-date-mode-label { font-size:12px; font-weight:600; color:${SLATE_MID}; }
    .aa-date-mode-sub   { font-size:11px; color:${MUTED}; margin-top:2px; }

    /* Alert overrides */
    .aa-modal .ant-alert { border-radius:10px !important; margin-bottom:16px !important; }

    /* Modal footer buttons */
    .aa-modal-cancel {
      height:40px !important; padding:0 20px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:600 !important;
      font-family:'DM Sans',sans-serif !important;
      border:1.5px solid #e2e8f0 !important; color:${SLATE_MID} !important;
    }
    .aa-modal-approve {
      height:40px !important; padding:0 24px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:700 !important;
      font-family:'Sora',sans-serif !important;
      background:linear-gradient(135deg,${EMERALD},${TEAL}) !important;
      border:none !important; color:${WHITE} !important;
      box-shadow:0 4px 12px ${TEAL}44 !important;
    }
    .aa-modal-reject {
      height:40px !important; padding:0 24px !important; border-radius:10px !important;
      font-size:13px !important; font-weight:700 !important;
      font-family:'Sora',sans-serif !important;
      background:linear-gradient(135deg,#b91c1c,#dc2626) !important;
      border:none !important; color:${WHITE} !important;
      box-shadow:0 4px 12px #ef444444 !important;
    }

    /* Animations */
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    .aa-fade   { animation:fadeUp .5s ease both; }
    .aa-fade-1 { animation-delay:.05s; }
    .aa-fade-2 { animation-delay:.12s; }
  `;
  document.head.appendChild(el);
};

/* ─── Main component ──────────────────────────────────────────── */
const AppointmentAdmin = () => {
  const dispatch = useDispatch();
  const [appointments,   setAppointments]   = useState([]);
  const [filteredInfo,   setFilteredInfo]   = useState({});
  const [sortedInfo,     setSortedInfo]     = useState({});
  const [searchTerm,     setSearchTerm]     = useState("");
  const [statusFilter,   setStatusFilter]   = useState("All");
  const [open,           setOpen]           = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [actionStatus,   setActionStatus]   = useState("");
  const [useCustomDate,  setUseCustomDate]  = useState(false);
  const [form] = Form.useForm();

  useEffect(() => { injectStyles(); }, []);

  /* ── Exports ── */
  const exportToCSV = () => {
    const exportData = filteredData.map(a => ({
      Patient: a.userInfo?.name || "N/A",
      "Appointment Service": a.doctorInfo?.service || "N/A",
      "Doctor Name": `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`,
      "Clinic Day": a.doctorInfo?.clinicDay?.join(", ") || "N/A",
      "Scheduled Date": a.schedule_date ? moment(a.schedule_date).format("DD-MM-YYYY") : "Not set",
      Status: a.status,
      "Progress (%)": getStatusProgress(a.status),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Appointments");
    const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "appointments.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = ["Patient", "Service", "Doctor", "Clinic Day", "Date", "Status", "Progress"];
    const tableRows = filteredData.map(a => [
      a.userInfo?.name || "N/A",
      a.doctorInfo?.service || "N/A",
      `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`,
      a.doctorInfo?.clinicDay?.join(", ") || "N/A",
      a.schedule_date ? moment(a.schedule_date).format("DD-MM-YYYY") : "Not set",
      a.status,
      `${getStatusProgress(a.status)}%`,
    ]);
    autoTable(doc, { head: [tableColumn], body: tableRows });
    doc.save("appointments.pdf");
  };

  /* ── Data ── */
  const getAppointmentData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.get("/api/admin/get-all-appointments", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (res.data.success) setAppointments(res.data.data);
    } catch { dispatch(hideLoading()); }
  }, [dispatch]);

  useEffect(() => { getAppointmentData(); }, [getAppointmentData]);

  const handleTableChange = (_, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const showModal = (record, status) => {
    setSelectedRecord(record);
    setActionStatus(status);
    form.resetFields();
    setOpen(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const { comment, schedule_date } = values;
      if (actionStatus === "approved" && !schedule_date) { toast.error("Please pick a schedule date"); return; }
      setConfirmLoading(true);
      dispatch(showLoading());
      await axios.post(
        "/api/doctor/change-appointment-status",
        {
          appointmentId: selectedRecord._id,
          status: actionStatus,
          comment,
          schedule_date: schedule_date ? schedule_date.format("YYYY-MM-DD") : undefined,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      toast.success(`Appointment ${actionStatus}`);
      setOpen(false);
      setConfirmLoading(false);
      form.resetFields();
      getAppointmentData();
    } catch {
      dispatch(hideLoading());
      toast.error("Something went wrong");
      setConfirmLoading(false);
    }
  };

  const filteredData = appointments.filter(a => {
    const matchesSearch = a.userInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const clinicMatcher = useMemo(() => {
    const rules    = selectedRecord?.doctorInfo?.clinicDay || [];
    const matchers = rules.map(ruleToMatcher);
    return (dateMoment) => matchers.some(fn => fn(dateMoment));
  }, [selectedRecord]);

  const disableNonClinicDays = (current) =>
    !current || !current.isValid() ||
    current < moment().startOf("day") ||
    !clinicMatcher(current);

  const nextClinicDate = useMemo(() => {
    let d = moment().startOf("day");
    for (let i = 0; i < 30; i++) { if (clinicMatcher(d)) return d; d.add(1, "day"); }
    return null;
  }, [clinicMatcher]);

  /* ── Summary counts ── */
  const counts = appointments.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  /* ── Columns ── */
  const columns = [
    {
      title: "Patient",
      key: "patient",
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar size={32} style={{ background: `linear-gradient(135deg,${EMERALD},${TEAL})`, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {r.userInfo?.name?.[0]?.toUpperCase()}
          </Avatar>
          <Text strong style={{ fontSize: 13, color: SLATE }}>{r.userInfo?.name || "N/A"}</Text>
        </div>
      ),
      sorter: (a, b) => (a.userInfo?.name || "").localeCompare(b.userInfo?.name || ""),
      sortOrder: sortedInfo.columnKey === "patient" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Patient No.",
      key: "phone",
      render: (_, r) => <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.userInfo?.phone || "—"}</Text>,
      filters: [...new Set(appointments.map(a => a.userInfo?.phone))].filter(Boolean).map(s => ({ text: s, value: s })),
      filteredValue: filteredInfo.phone || null,
      onFilter: (value, record) => record.userInfo?.phone === value,
      ellipsis: true,
    },
    {
      title: "Service",
      key: "service",
      render: (_, r) => <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.doctorInfo?.service || "—"}</Text>,
      filters: [...new Set(appointments.map(a => a.doctorInfo?.service))].filter(Boolean).map(s => ({ text: s, value: s })),
      filteredValue: filteredInfo.service || null,
      onFilter: (value, record) => record.doctorInfo?.service === value,
      sorter: (a, b) => (a.doctorInfo?.service || "").localeCompare(b.doctorInfo?.service || ""),
      sortOrder: sortedInfo.columnKey === "service" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Doctor",
      key: "doctorName",
      render: (_, r) => (
        <Text style={{ fontSize: 13, color: SLATE_MID }}>
          Dr. {r.doctorInfo?.firstName || ""} {r.doctorInfo?.lastName || ""}
        </Text>
      ),
      sorter: (a, b) =>
        `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`.localeCompare(
          `${b.doctorInfo?.firstName || ""} ${b.doctorInfo?.lastName || ""}`
        ),
      sortOrder: sortedInfo.columnKey === "doctorName" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Location",
      key: "location",
      render: (_, r) => <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.doctorInfo?.location || "—"}</Text>,
      filters: [...new Set(appointments.map(a => a.doctorInfo?.location))].filter(Boolean).map(s => ({ text: s, value: s })),
      filteredValue: filteredInfo.location || null,
      onFilter: (value, record) => record.doctorInfo?.location === value,
      ellipsis: true,
    },
    {
      title: "Progress",
      key: "progress",
      render: (_, r) => {
        const cfg = getStatusCfg(r.status);
        return (
          <Tooltip title={`${cfg.progress}% — ${r.status}`}>
            <div style={{ minWidth: 90 }}>
              <Progress percent={cfg.progress} strokeColor={cfg.bar} size="small" showInfo={false} />
              <Text style={{ fontSize: 11, display: "block", textAlign: "center", marginTop: 2, color: cfg.bar, fontWeight: 700 }}>
                {cfg.progress}%
              </Text>
            </div>
          </Tooltip>
        );
      },
      sorter: (a, b) => getStatusProgress(a.status) - getStatusProgress(b.status),
      sortOrder: sortedInfo.columnKey === "progress" ? sortedInfo.order : null,
      width: 110,
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => {
        const cfg = getStatusCfg(r.status);
        return (
          <span className="aa-badge" style={{ background: cfg.bg, color: cfg.color }}>
            {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
          </span>
        );
      },
      width: 110,
    },
    {
      title: "Scheduled",
      key: "schedule_date",
      render: (_, r) =>
        (r.status === "approved" || r.status === "follow-up") && r.schedule_date
          ? <Text style={{ fontSize: 12, color: SLATE_MID }}>{moment(r.schedule_date).format("ddd, MMM D, YYYY")}</Text>
          : <Text style={{ fontSize: 12, color: MUTED }}>—</Text>,
      sorter: (a, b) => moment(a.schedule_date || 0).unix() - moment(b.schedule_date || 0).unix(),
      sortOrder: sortedInfo.columnKey === "schedule_date" ? sortedInfo.order : null,
    },
    {
      title: "Follow-Up",
      key: "followUpDate",
      render: (_, r) =>
        r.status === "follow-up" && r.followUpDate
          ? <Text style={{ fontSize: 12, color: "#7c3aed" }}>{moment(r.followUpDate).format("ddd, MMM D, YYYY")}</Text>
          : <Text style={{ fontSize: 12, color: MUTED }}>—</Text>,
      sorter: (a, b) => moment(a.followUpDate || 0).unix() - moment(b.followUpDate || 0).unix(),
      sortOrder: sortedInfo.columnKey === "followUpDate" ? sortedInfo.order : null,
    },
    {
      title: "Actions",
      key: "action",
      width: 160,
      render: (_, r) =>
        r.status === "pending" ? (
          <Space>
            <Button className="aa-approve-btn" icon={<CheckCircleOutlined />} onClick={() => showModal(r, "approved")}>
              Approve
            </Button>
            <Button className="aa-reject-btn" icon={<CloseCircleOutlined />} onClick={() => showModal(r, "rejected")}>
              Reject
            </Button>
          </Space>
        ) : null,
    },
  ];

  const isApprove = actionStatus === "approved";

  return (
    <Layout>
      <div className="aa-root">
        <div className="aa-wrap">

          {/* Hero */}
          <div className="aa-hero aa-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="aa-eyebrow"><CalendarOutlined style={{ marginRight: 5 }} />Administration</div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora',sans-serif", color: WHITE, fontWeight: 800 }}>
                Appointments
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Manage, approve, and track all patient appointment requests
              </Text>
              <div className="aa-hero-badges">
                <div className="aa-hero-badge"><CalendarOutlined style={{ marginRight: 5 }} />{appointments.length} Total</div>
                {counts.pending  && <div className="aa-hero-badge" style={{ background: "#fef9c3cc", color: "#b45309" }}>⏳ {counts.pending} Pending</div>}
                {counts.approved && <div className="aa-hero-badge" style={{ background: `${TEAL_LIGHT}cc`, color: EMERALD }}>✓ {counts.approved} Approved</div>}
                {counts.completed && <div className="aa-hero-badge" style={{ background: "#dcfce7cc", color: "#16a34a" }}>✔ {counts.completed} Completed</div>}
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="aa-toolbar aa-fade aa-fade-1">
            <Input
              className="aa-search"
              prefix={<SearchOutlined style={{ color: MUTED }} />}
              placeholder="Search patient..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              allowClear
            />
            <Select
              className="aa-status-select"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 160 }}
              options={[
                { value: "All",       label: "All Statuses" },
                { value: "pending",   label: "Pending"      },
                { value: "approved",  label: "Approved"     },
                { value: "rejected",  label: "Rejected"     },
                { value: "follow-up", label: "Follow-up"    },
                { value: "completed", label: "Completed"    },
              ]}
            />
            <Button className="aa-btn aa-btn-excel" icon={<FileExcelOutlined />} onClick={exportToCSV}>Excel</Button>
            <Button className="aa-btn aa-btn-pdf"   icon={<FilePdfOutlined />}   onClick={exportToPDF}>PDF</Button>
            <Button className="aa-btn" icon={<FilterOutlined />} onClick={() => setFilteredInfo({})}>Filters</Button>
            <Button className="aa-btn" icon={<ClearOutlined />}  onClick={() => { setFilteredInfo({}); setSortedInfo({}); }}>Reset</Button>
          </div>

          {/* Table */}
          <div className="aa-table-wrap aa-fade aa-fade-2">
            <Table
              columns={columns}
              dataSource={filteredData.map(a => ({ ...a, key: a._id }))}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
              pagination={{ pageSize: 10, showSizeChanger: false }}
            />
          </div>

          {/* Approve / Reject Modal */}
          <Modal
            className={`aa-modal ${isApprove ? "approve-modal" : "reject-modal"}`}
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: isApprove ? TEAL_LIGHT : "#fee2e2",
                  color: isApprove ? EMERALD : "#dc2626",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {isApprove ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                </div>
                <span>{isApprove ? "Approve Appointment" : "Reject Appointment"}</span>
              </div>
            }
            open={open}
            onCancel={() => { setOpen(false); setUseCustomDate(false); form.resetFields(); }}
            footer={[
              <Button key="cancel" className="aa-modal-cancel"
                onClick={() => { setOpen(false); setUseCustomDate(false); form.resetFields(); }}>
                Cancel
              </Button>,
              <Button key="ok" className={isApprove ? "aa-modal-approve" : "aa-modal-reject"}
                loading={confirmLoading} onClick={handleOk}
                icon={isApprove ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                {isApprove ? "Approve" : "Reject"}
              </Button>,
            ]}
            width={560}
            destroyOnClose
          >
            <Form form={form} layout="vertical" requiredMark={false}>

              <Form.Item label="Comment" name="comment"
                rules={[{ required: true, message: "Please add a comment" }]}>
                <Input.TextArea rows={4} placeholder="Add a comment or reason..." />
              </Form.Item>

              {isApprove && (
                <>
                  {/* Date mode toggle */}
                  <div className="aa-date-mode-strip">
                    <div>
                      <div className="aa-date-mode-label">Clinic days only</div>
                      <div className="aa-date-mode-sub">Respect doctor's available days</div>
                    </div>
                    <Switch
                      checked={useCustomDate}
                      onChange={setUseCustomDate}
                      checkedChildren="Custom"
                      unCheckedChildren="Clinic"
                      style={{ background: useCustomDate ? "#f97316" : TEAL }}
                    />
                    <div style={{ textAlign: "right" }}>
                      <div className="aa-date-mode-label">Custom date</div>
                      <div className="aa-date-mode-sub">Override clinic days</div>
                    </div>
                  </div>

                  <Form.Item
                    label={
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        Schedule Date
                        <span style={{
                          padding: "1px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                          background: useCustomDate ? "#fff7ed" : TEAL_LIGHT,
                          color: useCustomDate ? "#f97316" : EMERALD,
                        }}>
                          {useCustomDate ? "Custom" : "Clinic Days"}
                        </span>
                      </span>
                    }
                    name="schedule_date"
                    rules={[{ required: true, message: "Please choose a schedule date" }]}
                  >
                    <DatePicker
                      disabledDate={useCustomDate ? undefined : disableNonClinicDays}
                      placeholder={
                        useCustomDate
                          ? "Select any date"
                          : `Clinic days: ${selectedRecord?.doctorInfo?.clinicDay?.join(", ")}`
                      }
                      format="dddd, MMMM Do, YYYY"
                      suffixIcon={<CalendarOutlined style={{ color: MUTED }} />}
                    />
                  </Form.Item>

                  {!useCustomDate && nextClinicDate && (
                    <Alert
                      message={<span>Next clinic day: <Text strong>{nextClinicDate.format("dddd, MMMM D, YYYY")}</Text></span>}
                      type="info" showIcon
                      icon={<InfoCircleOutlined />}
                    />
                  )}
                  {useCustomDate && (
                    <Alert
                      message="Overriding clinic days. Ensure the doctor is available on this date."
                      type="warning" showIcon
                      icon={<WarningOutlined />}
                    />
                  )}
                </>
              )}
            </Form>
          </Modal>

        </div>
      </div>
    </Layout>
  );
};

export default AppointmentAdmin;